import React, { useState, useEffect } from 'react';
import '@/App.css';
import '@/styles/mobile-polish.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import axios from 'axios';
import apiCache, { cacheKeyForRequest, isCacheableRequest, invalidateForMutation } from './utils/apiCache';

// Components
import LoginScreen from './components/LoginScreenLuxe';
import InstallPrompt from './components/InstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import MainApp from './components/MainApp';

// Utils
import { getUserSession, setUserSession, isSessionValid } from './utils/session';

// Context
import { AppProvider } from './context/AppStateContext';
import { UploadProvider } from './context/UploadContext';
import { initGlobalHaptics } from './utils/haptics';
import AppDialogs from './components/common/AppDialogs';
import FloatingUploadIndicator from './components/upload/FloatingUploadIndicator';

// Globalny interceptor — dorzuca nagłówki audytu do każdego requestu.
axios.interceptors.request.use((config) => {
  try {
    const sess = getUserSession();
    const u = sess?.user;
    if (u) {
      config.headers = config.headers || {};
      if (u.id) config.headers['X-User-Id'] = u.id;
      if (u.username) config.headers['X-User-Username'] = u.username;
      if (u.location) config.headers['X-User-Location'] = u.location;
    }
  } catch { /* nic */ }

  // ===== Cache GET-ów =====
  if (isCacheableRequest(config)) {
    const key = cacheKeyForRequest(config);
    const cached = apiCache.get(key);
    if (cached) {
      // Skróć fetch — zwracamy cached response przez adapter
      config.adapter = () => Promise.resolve({
        data: cached.data,
        status: cached.status || 200,
        statusText: 'OK (cache)',
        headers: cached.headers || {},
        config,
        request: { fromCache: true },
      });
      return config;
    }
    // Deduplikacja — równoległe żądania współdzielą Promise
    const pending = apiCache.pending(key);
    if (pending) {
      config.adapter = () => pending.then((res) => ({
        ...res, config, request: { fromInflight: true },
      }));
      return config;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const config = response.config || {};
    const url = config.url || '';
    const method = (config.method || 'GET').toUpperCase();
    if (method === 'GET' && isCacheableRequest(config) && !response.request?.fromCache && !response.request?.fromInflight) {
      const key = cacheKeyForRequest(config);
      apiCache.set(key, { data: response.data, status: response.status, headers: response.headers });
    }
    // Mutacje — unieważnij cache zasobu
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && url.includes('/api/')) {
      invalidateForMutation(url);
    }
    return response;
  },
  (error) => {
    // Mutacje błędne też mogą zmieniać stan — bezpiecznie wywalamy cache
    const config = error?.config || {};
    const url = config.url || '';
    const method = (config.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && url.includes('/api/')) {
      invalidateForMutation(url);
    }
    return Promise.reject(error);
  }
);

// ============ ROOT APP ============
function App() {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState('');
  const [checking, setChecking] = useState(true);

  // Globalny haptic feedback dla toastów (mobile vibration)
  useEffect(() => { initGlobalHaptics(); }, []);

  useEffect(() => {
    if (isSessionValid()) {
      const session = getUserSession();
      const loc = session.location || '';

      // Validate location - if it's a number (timestamp bug), clear session
      if (typeof loc === 'number' || (loc && !['nowysacz', 'krakow', 'grodek'].includes(loc))) {
        console.error('Invalid location in session:', loc);
        setUserSession(null);
        setUser(null);
        setLocation('');
        setChecking(false);
        return;
      }

      setUser(session.user);
      setLocation(loc);
    }
    setChecking(false);
  }, []);

  const handleLogin = (loggedInUser, selectedLocation) => {
    // Validate location parameter
    if (!selectedLocation || !['nowysacz', 'krakow', 'grodek'].includes(selectedLocation)) {
      console.error('Invalid location provided to handleLogin:', selectedLocation);
      alert('Błąd: Nieprawidłowa lokalizacja. Spróbuj ponownie.');
      return;
    }

    setUser(loggedInUser);
    setLocation(selectedLocation);

    // Update session with location
    setUserSession({
      user: loggedInUser,
      location: selectedLocation,
      until: Date.now() + 8 * 60 * 60 * 1000
    });
  };

  const handleLogout = () => {
    setUserSession(null);
    setUser(null);
    setLocation('');
    apiCache.invalidateAll();
  };

  if (checking) return <div className="login-container"><div className="login-card">Ładowanie...</div></div>;
  if (!user) return <LoginScreen onLogin={handleLogin} />;

  return (
    <AppProvider user={user} location={location}>
      <UploadProvider>
        <AppDialogs />
        <MainApp user={user} location={location} onLogout={handleLogout} />
        <FloatingUploadIndicator />
      </UploadProvider>
    </AppProvider>
  );
}

// React Query Client Configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Enhanced App with React Query Provider and Toast System
function AppWithProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary fallbackMessage="Wystąpił błąd podczas ładowania aplikacji. Spróbuj odświeżyć stronę.">
        <App />
        <InstallPrompt />
        <Toaster
          position="bottom-center"
          expand={true}
          richColors
          closeButton
          toastOptions={{
            style: {
              fontSize: '15px',
              padding: '16px',
              minWidth: '300px',
            },
          }}
        />
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default AppWithProviders;
